import { isEmpty } from "lodash";
import { useCallback, useContext, useState } from "react";
import { ERROR } from "services/common/Constants";
import { extractSecurityRatingErrorCode, SecurityRatingConfigStatus, SecurityRatingContext } from "services/common/SecurityRatingUtils";
import { evaluateSecurityRating, getAccountCompaniesForScope, updateThirdpartySecurityRatingConfig } from "services/edge/SecurityRatingService";
import { Context } from "states/Store";
import SecurityRatingForm from "./SecurityRatingForm";
import SecurityRatingSharableCompanies from "./SecurityRatingSharableCompanies";

const SecurityRatingActivationForm = ({goBack, thirdPartyId}) => {
  const [isLoading, setLoading] = useState(false);
  const [state, dispatch] = useContext(Context);
  const [sharableCompanies, setSharableCompanies] = useState();

  const { config, setConfig } = useContext(SecurityRatingContext);

  const updateConfig = useCallback(async (data) => {
    try {
      const conf = await updateThirdpartySecurityRatingConfig(
        state.account.id,
        thirdPartyId,
        data
      );
      setConfig(conf.content)
    } catch (error) {
      if (error?.message?.status === 404) {
        setConfig({})
      } else {
        dispatch({
          type: "ALERT",
          alert: { type: ERROR, msg: error?.message?.messages?.error?.[0]?.code || "INTERNAL_ERROR" },
        });
      }
    }
  }, [state.account.id, thirdPartyId, setConfig, dispatch])

  const getAccountCompanies = useCallback(async (companyScope) => {
    try {
      const accountCompanies = await getAccountCompaniesForScope(
        state.account.id,
        companyScope
      );
      return accountCompanies.content;
    } catch (error) {
      dispatch({
        type: "ALERT",
        alert: { type: ERROR, msg: error?.message?.messages?.error?.[0]?.code || "INTERNAL_ERROR" },
      });
    }
  }, [state.account.id, dispatch])


  // Only update config on form change when it's not active, it will be changed for active configs on evaluation.
  const onFormUpdate = async (data) => {
    if (config?.status !== SecurityRatingConfigStatus.ACTIVE) {
      await updateConfig(data);
    }
  };

  const onValidation = async (data) => {
    setLoading(true)

    const companies = await getAccountCompanies(data.companyScope);

    if (!isEmpty(companies)) {
      setSharableCompanies(companies)
    } else {
      setSharableCompanies([]);
      await startOrUpdateRating(data)
    }

    setLoading(false)
  }

  const startOrUpdateRating = async (config) => {
    try {
      await evaluateSecurityRating(
        state.account.id,
        thirdPartyId,
        config
      );
      setConfig({ ...config, status: SecurityRatingConfigStatus.ACTIVE });
      goBack();
    } catch (error) {
      const securityRatingErrorCode = extractSecurityRatingErrorCode(error?.message?.messages?.error?.[0]?.message);
      dispatch({
        type: "ALERT",
        alert: { type: ERROR, msg: securityRatingErrorCode ? `securityRating.errors.${securityRatingErrorCode}` : "INTERNAL_ERROR" },
      });
    }
  };

  if (isEmpty(sharableCompanies)) {
    return <SecurityRatingForm
      onFormUpdate={onFormUpdate}
      onSubmit={onValidation}
      isLoading={isLoading}
      goBack={goBack}
    />
  } else {
    return <SecurityRatingSharableCompanies
      onFormUpdate={onFormUpdate}
      onSubmit={startOrUpdateRating}
      isLoading={isLoading}
      setLoading={setLoading}
      sharableCompanies={sharableCompanies}
      goBack={() => setSharableCompanies([])}
      thirdPartyId={thirdPartyId}
    />
  }

}

export default SecurityRatingActivationForm;